// src/services/api/apiClient.ts
import { apiConfig } from '../../config/api';

/**
 * Get JWT authorization headers for API requests.
 * Uses access_token from localStorage.
 */
function getAuthHeaders(): HeadersInit {
  const headers: HeadersInit = {};
  const accessToken = localStorage.getItem('access_token');
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }
  return headers;
}

/**
 * Handle 401 Unauthorized by refreshing token and retrying request.
 * This is the reactive refresh fallback for expired tokens.
 *
 * @param url - Original request URL
 * @param options - Original request options
 * @returns Retried fetch response
 * @throws Error if refresh fails
 */
async function handle401Response(
  url: string,
  options: RequestInit
): Promise<Response> {
  console.log('[apiClient] 401 detected, attempting token refresh');

  // Get refresh token
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) {
    console.error('[apiClient] No refresh token available for retry');
    throw new Error('No refresh token available');
  }

  // Attempt token refresh
  const refreshResponse = await fetch(`${apiConfig.baseUrl}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });

  if (!refreshResponse.ok) {
    // Refresh failed, cannot retry
    console.error('[apiClient] Token refresh failed during 401 retry');
    throw new Error('Token refresh failed');
  }

  const refreshData = await refreshResponse.json();

  // Store new access token
  localStorage.setItem('access_token', refreshData.access_token);
  const expiryTime = Date.now() + (refreshData.expires_in * 1000);
  localStorage.setItem('token_expiry', expiryTime.toString());

  console.log('[apiClient] Token refreshed, retrying original request');

  // Retry original request with new token
  const retryHeaders = new Headers(options.headers);
  retryHeaders.set('Authorization', `Bearer ${refreshData.access_token}`);

  const retryResponse = await fetch(url, {
    ...options,
    headers: retryHeaders,
  });

  return retryResponse;
}

/**
 * Generic API client for making requests to the Mythbound backend.
 * Handles base URL, timeouts, and ensures credentials (cookies/headers) are included.
 */
async function buildApiError(response: Response, baseMessage: string): Promise<Error> {
  let detail: any = null;
  try {
    detail = await response.json();
  } catch {
    try {
      detail = await response.text();
    } catch {
      detail = null;
    }
  }
  const error = new Error(baseMessage);
  (error as any).status = response.status;
  (error as any).detail = detail;
  return error;
}

export const apiClient = {
  async get(endpoint: string, options: RequestInit = {}) {
    try {
      const url = `${apiConfig.baseUrl}${endpoint}`;
      const requestOptions: RequestInit = {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          ...getAuthHeaders(),
          ...options.headers,
        },
        signal: AbortSignal.timeout(apiConfig.timeout), // Use AbortSignal for timeout
        credentials: 'include', // Keep for backward compatibility
        ...options,
      };

      let response = await fetch(url, requestOptions);

      // Handle 401 with token refresh + retry
      if (response.status === 401) {
        console.log('[apiClient] GET request unauthorized, attempting refresh');
        response = await handle401Response(url, requestOptions);
      }

      if (!response.ok) {
        throw await buildApiError(response, `API GET request failed: ${response.status}`);
      }

      // Handle empty responses (e.g., 204 No Content)
      if (response.status === 204) {
          return null;
      }

      return await response.json();
    } catch (error) {
      console.error('[apiClient] GET request failed:', error);
      throw error;
    }
  },

  async post(endpoint: string, body: any, options: RequestInit = {}) {
    try {
      const url = `${apiConfig.baseUrl}${endpoint}`;
      const requestOptions: RequestInit = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...getAuthHeaders(),
          ...options.headers,
        },
        body: JSON.stringify(body),
        signal: AbortSignal.timeout(apiConfig.timeout),
        credentials: 'include', // Keep for backward compatibility
        ...options,
      };

      let response = await fetch(url, requestOptions);

      // Handle 401 with token refresh + retry
      if (response.status === 401) {
        console.log('[apiClient] POST request unauthorized, attempting refresh');
        response = await handle401Response(url, requestOptions);
      }

      if (!response.ok) {
        throw await buildApiError(response, `API POST request failed: ${response.status}`);
      }

      if (response.status === 204) {
          return null;
      }

      return await response.json();
    } catch (error) {
      console.error('[apiClient] POST request failed:', error);
      throw error;
    }
  },

  async put(endpoint: string, body: any, options: RequestInit = {}) {
    try {
      const url = `${apiConfig.baseUrl}${endpoint}`;
      const requestOptions: RequestInit = {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...getAuthHeaders(),
          ...options.headers,
        },
        body: JSON.stringify(body),
        signal: AbortSignal.timeout(apiConfig.timeout),
        credentials: 'include', // Keep for backward compatibility
        ...options,
      };

      let response = await fetch(url, requestOptions);

      // Handle 401 with token refresh + retry
      if (response.status === 401) {
        console.log('[apiClient] PUT request unauthorized, attempting refresh');
        response = await handle401Response(url, requestOptions);
      }

      if (!response.ok) {
        throw await buildApiError(response, `API PUT request failed: ${response.status}`);
      }

      if (response.status === 204) {
          return null;
      }

      return await response.json();
    } catch (error) {
      console.error('[apiClient] PUT request failed:', error);
      throw error;
    }
  },
  
  async delete(endpoint: string, options: RequestInit = {}) {
    try {
      const url = `${apiConfig.baseUrl}${endpoint}`;
      const requestOptions: RequestInit = {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
           ...options.headers,
        },
        signal: AbortSignal.timeout(apiConfig.timeout),
        credentials: 'include', // Keep for backward compatibility
        ...options,
      };

      let response = await fetch(url, requestOptions);

      // Handle 401 with token refresh + retry
      if (response.status === 401) {
        console.log('[apiClient] DELETE request unauthorized, attempting refresh');
        response = await handle401Response(url, requestOptions);
      }

      if (!response.ok) {
        throw await buildApiError(response, `API DELETE request failed: ${response.status}`);
      }

      if (response.status === 204) {
          return null;
      }

      // DELETE might not return a body, handle accordingly
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      } else {
        return null; // Or response.text() if applicable
      }
    } catch (error) {
      console.error('[apiClient] DELETE request failed:', error);
      throw error;
    }
  }
};
